import { useMemo, useState } from 'react';
import { CustomAccordion } from 'components/Accordion';
import { TileContent } from 'components/Layout/PageContainer';
import { Pagination } from '@mui/material';
import { TbIcon } from 'components/Figma';

interface PaginatedAccordionListProps<T> {
  items: T[];
  getId: (item: T) => string;
  getTitle: (item: T) => string;
  getBadgeCount: (item: T) => number;
  getChildrenItems: (item: T) => React.ReactNode[];
  childrenLimit: number;
  accordionItemLimit: number;
}

export function PaginatedAccordionList<T>({
  items,
  getId,
  getTitle,
  getBadgeCount,
  getChildrenItems,
  childrenLimit,
  accordionItemLimit,
}: PaginatedAccordionListProps<T>) {
  const [currentOuterPage, setCurrentOuterPage] = useState(1);
  const [innerPages, setInnerPages] = useState<Record<string, number>>({});

  const sortedItems = [...items].sort((a, b) => getBadgeCount(b) - getBadgeCount(a));
  const paginatedOuter = sortedItems.slice(
    (currentOuterPage - 1) * accordionItemLimit,
    currentOuterPage * accordionItemLimit
  );

  const maxHeight = useMemo(() => {
    const maxRows = Math.min(accordionItemLimit, sortedItems.length);
    return maxRows * 52 + 38;
  }, [sortedItems, accordionItemLimit]);

  const accordionItems = paginatedOuter.map(item => {
    const id = getId(item);
    const title = getTitle(item);
    const badgeCount = getBadgeCount(item);
    const children = getChildrenItems(item);

    const totalPages = Math.ceil(children.length / childrenLimit);
    const currentPage = innerPages[id] || 1;
    const startIndex = (currentPage - 1) * childrenLimit;
    const paginatedChildren = children.slice(startIndex, startIndex + childrenLimit);

    return {
      id,
      title: (
        <div className="flex items-center w-full font-bold">
          <span className="w-[140px] hover:min-w-fit inline-block truncate mr-2">{title}</span>
          <span className="bg-gray-200 px-2 py-1 rounded-full text-sm font-bold">{badgeCount}</span>
        </div>
      ),
      children: (
        <div className="flex items-center mt-2 px-4">
          <div className="w-[24px] flex justify-center">
            {totalPages > 1 && (
              <button
                type="button"
                onClick={() =>
                  setInnerPages(prev => ({
                    ...prev,
                    [id]: Math.max(1, currentPage - 1),
                  }))
                }
                disabled={currentPage === 1}
                className="disabled:opacity-30"
              >
                <TbIcon name="ChevronLeft" size="lg" />
              </button>
            )}
          </div>

          <div className="flex-1 flex justify-center gap-2 overflow-hidden">{paginatedChildren}</div>

          <div className="w-[24px] flex justify-center">
            {totalPages > 1 && (
              <button
                type="button"
                onClick={() =>
                  setInnerPages(prev => ({
                    ...prev,
                    [id]: Math.min(totalPages, currentPage + 1),
                  }))
                }
                disabled={currentPage === totalPages}
                className="disabled:opacity-30"
              >
                <TbIcon name="ChevronRight" size="lg" />
              </button>
            )}
          </div>
        </div>
      ),
    };
  });

  return (
    <TileContent className="flex flex-col items-center p-0">
      <div className="w-full mt-6" style={{ height: `${maxHeight}px` }}>
        <CustomAccordion alwaysKeepOneExpanded items={accordionItems} defaultExpandedId={accordionItems[0]?.id} />
      </div>
      {items.length > accordionItemLimit && (
        <Pagination
          className="mt-4"
          count={Math.ceil(items.length / accordionItemLimit)}
          page={currentOuterPage}
          onChange={(_e, p) => setCurrentOuterPage(p)}
          size="small"
        />
      )}
    </TileContent>
  );
}
